import { lazy, ReactElement } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';

import Loadable from 'components/Loadable';
import AdminLayout from 'layout/AdminLayout';
import useAuth from 'hooks/useAuth';

const Login = Loadable(lazy(() => import('pages/login')));
const Dashboard = Loadable(lazy(() => import('pages/admin/dashboard')));
const Reports = Loadable(lazy(() => import('pages/admin/reports')));
const Drilldown = Loadable(lazy(() => import('pages/admin/drilldown')));
const SalesPro = Loadable(lazy(() => import('pages/admin/salespro')));
const Rates = Loadable(lazy(() => import('pages/admin/rates')));
const AgentLlmConfig = Loadable(lazy(() => import('pages/admin/agent-llm-config')));
const Promotion = Loadable(lazy(() => import('pages/admin/promotion')));
const Companies = Loadable(lazy(() => import('pages/admin/companies')));
const Users = Loadable(lazy(() => import('pages/admin/users')));
const Assistant = Loadable(lazy(() => import('pages/admin/assistant')));
const ApiKeys = Loadable(lazy(() => import('pages/admin/api-keys')));
const UserDetail = Loadable(lazy(() => import('pages/admin/users/userDetail')));
const Partners = Loadable(lazy(() => import('pages/admin/partners')));
const PartnerDetail = Loadable(lazy(() => import('pages/admin/partners/PartnerDetail')));
const ProviderConnections = Loadable(lazy(() => import('pages/admin/provider-connections')));

// Protected pages are never painted (and never fire their requests) without a session.
const AuthGuard = ({ children }: { children: ReactElement }) => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  if (!isLoggedIn) return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  return children;
};

export default function Routes() {
  return useRoutes([
    { path: 'login', element: <Login /> },
    {
      path: '/',
      element: (
        <AuthGuard>
          <AdminLayout />
        </AuthGuard>
      ),
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        { path: 'dashboard', element: <Dashboard /> },
        { path: 'reports', element: <Reports /> },
        { path: 'drilldown', element: <Drilldown /> },
        { path: 'salespro', element: <SalesPro /> },
        { path: 'rates', element: <Rates /> },
        { path: 'agent-llm-config', element: <AgentLlmConfig /> },
        { path: 'provider-connections', element: <ProviderConnections /> },
        { path: 'promotion', element: <Promotion /> },
        { path: 'companies', element: <Companies /> },
        { path: 'users', element: <Users /> },
        { path: 'users/:userId', element: <UserDetail /> },
        { path: 'api-keys', element: <ApiKeys /> },
        { path: 'assistant', element: <Assistant /> },
        { path: 'partners', element: <Partners /> },
        { path: 'partners/:partnerId', element: <PartnerDetail /> },
        { path: '*', element: <Navigate to="/dashboard" replace /> }
      ]
    }
  ]);
}
